import { CheckIcon, cn } from '@commander/ui';
import type React from 'react';
import type { StepProgressProps } from '../../types/onboarding.types';

const StepProgress: React.FC<StepProgressProps> = ({
  steps,
  currentStep,
  completedSteps,
  className,
}) => {
  const getStepStatus = (index: number) => {
    if (index < currentStep) return 'completed';
    if (completedSteps.includes(index)) return 'completed';
    if (currentStep === index) return 'current';
    return 'pending';
  };

  const getStepIcon = (index: number) => {
    const status = getStepStatus(index);

    if (status === 'completed') {
      return <CheckIcon className="w-4 h-4 text-white" />;
    }

    if (status === 'current') {
      return (
        <div className="min-w-[24px] h-[24px] ring-1 ring-offset-[2px] ring-primary rounded-full bg-primary" />
      );
    }

    return <div className="w-3 h-3 rounded-full" />;
  };

  const getStepStyles = (index: number) => {
    const status = getStepStatus(index);

    return cn(
      'w-6 h-6 rounded-full flex items-center justify-center border-[1px] mb-[2px]',
      status === 'completed' && 'bg-primary ring-1 ring-offset-2 ring-primary border-primary',
      status === 'current' && 'ring-1 ring-offset-2 ring-primary border-primary bg-white',
      status === 'pending' && 'bg-primary/30 border-none'
    );
  };

  const getStepTextStyles = (index: number) => {
    const status = getStepStatus(index);

    return cn(
      'text-nav-text h-[17px] text-nowrap transition-colors',
      status === 'completed' || status === 'current' ? 'text-primary' : 'text-primary/50'
    );
  };

  const getConnectorStyles = (index: number) => {
    const isCompleted = index < currentStep;

    return cn(
      'w-[2px] h-[36px] mb-[4px] mt-[2px]',
      isCompleted ? 'bg-primary' : 'bg-primary/30',
      index === 0 && 'mt-[4px]'
    );
  };

  return (
    <nav className={cn('space-y-2', className)}>
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-start space-x-[10px] relative">
          <div className="relative flex flex-col items-center gap-2">
            <div className={getStepStyles(index)}>{getStepIcon(index)}</div>
            {index < steps.length - 1 && <div className={getConnectorStyles(index)} />}
          </div>
          <span className={getStepTextStyles(index)}>{step.title}</span>
        </div>
      ))}
    </nav>
  );
};

export default StepProgress;
